import {
  type ConnectionOperation,
  type DocumentAdapter,
  isEntityOperation,
  recordDocumentAdapter,
} from '@github-ui/optimistic-connection'

import {ItemType} from '../../../api/memex-items/item-type'
import type {MemexItemsPageQueryData} from './types'

/**
 * The board's own name for the collection its cards live in. A delete committed from the issue
 * viewer keys its mutation here, so the card disappears while the request is pending and comes back
 * on failure — the board never learns that a delete happened, only that this connection lost a node.
 */
export const MEMEX_ISSUE_DELETION_CONNECTION = {
  ownerId: 'memex-items',
  connectionPath: ['items'],
}

function deletedIssueId(ownerId: string, operation: ConnectionOperation): string | undefined {
  const {connectionPath: path, ownerId: owner} = MEMEX_ISSUE_DELETION_CONNECTION
  if (operation.type !== 'removeFromConnection' || ownerId !== owner) return undefined
  const matchesPath =
    operation.connectionPath.length === path.length &&
    operation.connectionPath.every((segment, index) => segment === path[index])
  return matchesPath ? operation.nodeId : undefined
}

type ItemsPage = {
  nodes: Array<{contentType: unknown; content: {id: number; globalRelayId?: unknown}}>
  pageInfo: {hasNextPage: boolean; hasPreviousPage: boolean}
}

function isDeletedIssue(item: ItemsPage['nodes'][number], issueId: string): boolean {
  return item.contentType === ItemType.Issue && item.content.globalRelayId === issueId
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

// Cache targets are unknown, unlike the typed array rendered by the hook. Validate only
// the representation this adapter reads, without claiming these are complete item models.
function isItemsPage(document: unknown): document is ItemsPage {
  return (
    isRecord(document) &&
    isRecord(document.pageInfo) &&
    typeof document.pageInfo.hasNextPage === 'boolean' &&
    typeof document.pageInfo.hasPreviousPage === 'boolean' &&
    Array.isArray(document.nodes) &&
    document.nodes.every(
      item =>
        isRecord(item) &&
        typeof item.contentType === 'string' &&
        isRecord(item.content) &&
        typeof item.content.id === 'number',
    )
  )
}

function removeIssueFromPage<T extends ItemsPage>(page: T, issueId: string): T {
  if (!page.nodes.some(item => isDeletedIssue(item, issueId))) return page
  return {...page, nodes: page.nodes.filter(item => !isDeletedIssue(item, issueId))}
}

/**
 * Memex holds its items as pages of denormalized cards rather than an id-owned GraphQL `nodes`
 * object, so the deletion declares how to find the issue in that shape. It carries the
 * representation only: match the issue's global relay id and drop that card. Group counts,
 * stream totals and the card pulled up from the next page are server-derived and reconcile through
 * Memex's coherent all-query refresh.
 */
export const memexIssueDeletionPageAdapter: DocumentAdapter = (document, change, context) => {
  if (!isItemsPage(document)) return undefined
  // Preserve the previous default entity behavior; deletion's global issue identity
  // must not become an identity/projection override for unrelated record fields.
  if (isEntityOperation(change.operation)) return recordDocumentAdapter(document, change, context)
  const issueId = deletedIssueId(change.ownerId, change.operation)
  if (issueId === undefined || !document.nodes.some(item => isDeletedIssue(item, issueId))) return undefined
  return {document: removeIssueFromPage(document, issueId)}
}

/** The same removal, over the array of loaded pages the board renders from. */
export const memexIssueDeletionPagesAdapter: DocumentAdapter<Array<MemexItemsPageQueryData>> = (
  document,
  change,
  context,
) => {
  if (isEntityOperation(change.operation)) return recordDocumentAdapter(document, change, context)
  const issueId = deletedIssueId(change.ownerId, change.operation)
  if (issueId === undefined || !document.some(page => page.nodes.some(item => isDeletedIssue(item, issueId)))) {
    return undefined
  }
  return {document: document.map(page => removeIssueFromPage(page, issueId))}
}
